import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './template-filters.module.scss';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE, ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {ControlledList} from '@Components/controlled-list';
import cloneDeep from 'lodash/cloneDeep';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {ControlledListItemType3} from '@Components/controlled-list/components/controlled-list-item-type-3';
import {ColorFill} from '@Components/color-fill';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import useWindowSize from '@Hooks/useWindowSize';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {useAppDispatch} from '@/hooks';
import {noop} from '@Utils/general.util';
import type {TemplateFilterData, TemplateFilterOptionData, TemplatesFiltersData} from '@Libraries/editor-templates-library';
import {ALL_FILTER_ID} from '@Libraries/editor-templates-library';
import {TemplateFiltersScrollButton} from '@Components/poster-editor/components/poster-add-item-bar/components/template-filters-scroll-button/template-filters-scroll-button';
import {hexToRgba} from '@Utils/color.util';
import {ChristmasFilter, HalloweenFilter} from '@Components/poster-editor/components/poster-add-item-bar/components/template-filters/template-filters.types';
import {ControlledListSeasonalItemType, IconPlacement} from '@Components/controlled-list/components/controlled-list-seasonal-item/controlled-list-seasonal-item.types';

interface TemplateFiltersProps {
  selectedFilters: Record<string, string>;
  filtersData?: TemplatesFiltersData;
  safeSearch?: boolean;
  isSafeSearchBeingUpdated: boolean;
  multipage?: boolean;
  isMultipageBeingUpdated?: boolean;

  onFilterUpdate(filters: Record<string, string>): void;

  onToggleSafeSearch(newValue: boolean): void;
  onToggleMultipage?: (newValue: boolean) => void;
}

export function TemplateFilters({isSafeSearchBeingUpdated = false, isMultipageBeingUpdated = false, ...props}: TemplateFiltersProps): ReactElement<any> {
  const [dropdownOpenState, setDropdownOpenState] = useState<string>();
  const {windowWidth} = useWindowSize();
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLUListElement | null>(null);
  const isMultiPageEnabled = window.PMW.isMultipageAvailable;

  const isSelectionMadeForList = (id: string): boolean => {
    return !!props.selectedFilters[id];
  };

  const stopClickPropagation = (e?: React.MouseEvent): void => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const getColorList = (id: string, data: TemplateFilterData, isSelectionMade: boolean): ReactElement<any> => {
    return (
      <ul className="spacing-m-0">
        <ControlledListItemType3
          onClick={(clickedId, e): void => {
            stopClickPropagation(e);
            itemClickHandler(id, ALL_FILTER_ID);
          }}
          id={ALL_FILTER_ID}
          text={window.i18next.t('pmwjs_all')}
          isSelected={!isSelectionMade}
          listSize={ControlledListItemSize.SMALL}
        />
        <li className="spacing-p-0">
          <ul className={`spacing-m-0 ${styles.colorGrid}`}>
            {data.optionsData.map((item) => {
              return (
                <li
                  onClick={(e) => {
                    stopClickPropagation(e);
                  }}
                  className={styles.colorContainers}
                  key={item.id.toString()}
                  id={item.id.toString()}
                >
                  <ColorFill
                    onClick={(): void => {
                      itemClickHandler(id, item.id.toString());
                    }}
                    isSelected={isSelectionMade && props.selectedFilters[id] === item.id.toString()}
                    className={styles.colorFill}
                    width={40}
                    height={40}
                    color={item.colorHex ? hexToRgba(item.colorHex) : []}
                  />
                </li>
              );
            })}
          </ul>
        </li>
      </ul>
    );
  };

  const itemClickHandler = (id: string, listItemId: string): void => {
    const newState = cloneDeep(props.selectedFilters);
    if (listItemId === ALL_FILTER_ID) {
      newState[id] = '';
    } else {
      newState[id] = listItemId;
    }

    props.onFilterUpdate(newState);

    if (windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
    setDropdownOpenState('');
  };

  const isAllFilterForDesignStyle = (id: string, itemId: string): boolean => {
    const designStyleId = 'design_style';
    const allItemId = '40';
    return id === designStyleId && itemId === allItemId;
  };

  const getSeasonType = (option: TemplateFilterOptionData): ControlledListSeasonalItemType => {
    if (option.filterOption === HalloweenFilter.type.valueOf()) {
      return ControlledListSeasonalItemType.HALLOWEEN;
    }
    return ControlledListSeasonalItemType.CHRISTMAS;
  };

  const isSeasonalListItem = (option: TemplateFilterOptionData): boolean => {
    return [HalloweenFilter.type.valueOf(), ChristmasFilter.type.valueOf()].includes(option.filterOption);
  };

  const getPopupForIdUpdated = (id: string): ReactElement<any> => {
    const listItems: ControlledListItemType[] = [];
    if (!props.filtersData) {
      return <></>;
    }
    const options = props.filtersData[id].optionsData;
    const isSelectionMade = isSelectionMadeForList(id);

    if (props.filtersData[id].isColorFilter) {
      return getColorList(id, props.filtersData[id], isSelectionMade);
    }

    listItems.push({
      id: ALL_FILTER_ID,
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_all'),
      isSelected: !isSelectionMade,
      listSize: ControlledListItemSize.SMALL,
    });

    for (const eachOption of options) {
      const listItemId = eachOption.id.toString();
      if (isAllFilterForDesignStyle(id, listItemId)) {
        continue;
      }
      if (isSeasonalListItem(eachOption)) {
        listItems.push({
          id: eachOption.id.toString(),
          type: CONTROLLED_LIST_ITEM_TYPE.SEASONAL,
          seasonType: getSeasonType(eachOption),
          text: eachOption.displayName,
          textClassName: styles.seasonalText,
          isSelected: isSelectionMade && props.selectedFilters[id] === listItemId,
          iconPlacement: IconPlacement.Right,
          listSize: ControlledListItemSize.SMALL,
        });
      } else {
        listItems.push({
          id: eachOption.id.toString(),
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
          text: eachOption.displayName,
          isSelected: isSelectionMade && props.selectedFilters[id] === listItemId,
          listSize: ControlledListItemSize.SMALL,
        });
      }
    }

    return (
      <ControlledList
        onClick={(listItemId, event): void => {
          event?.preventDefault();
          event?.stopPropagation();
          itemClickHandler(id, listItemId);
        }}
        className="spacing-m-0"
        id={id}
        items={listItems}
      />
    );
  };

  const onOpen = (id: string): void => {
    setDropdownOpenState(id);
  };

  const onClose = (): void => {
    setDropdownOpenState('');
  };

  const getOpenState = (id: string): boolean => {
    return dropdownOpenState === id;
  };

  const getFitlerDisplayNameFromId = (id: string, optionId: string): TemplateFilterOptionData | null => {
    if (!props.filtersData) {
      return null;
    }
    for (const eachItem of props.filtersData[id].optionsData) {
      if (optionId === String(eachItem.id)) {
        return eachItem;
      }
    }

    return null;
  };

  const removeFilterForId = (id: string): void => {
    const newState = cloneDeep(props.selectedFilters);
    newState[id] = '';
    props.onFilterUpdate(newState);
  };

  const getColorPalette = (color: string | undefined): ReactElement<any> | null => {
    if (!color) {
      return null;
    }

    return (
      <div
        className="radius-4 border-s-dark"
        style={{
          width: 16,
          height: 16,
          background: color,
        }}
      />
    );
  };

  const getSelector = (id: string): ReactElement<any> => {
    if (!props.filtersData) {
      throw new Error('no filters data found');
    }
    const isOpened = dropdownOpenState === id;
    const isFilterSelected = isSelectionMadeForList(id);
    let selectedFilter = null;
    if (isFilterSelected) {
      selectedFilter = getFitlerDisplayNameFromId(id, props.selectedFilters[id]);
    }

    return (
      <div
        key={id}
        className={`${styles.items} ${isFilterSelected ? styles.appliedFilter : ''} ${isOpened ? styles.active : ''} hover-transition-all _cursor-pointer radius-round flex-center spacing-p-r-3 spacing-p-l-3`}
      >
        {props.filtersData[id].isColorFilter && isFilterSelected && selectedFilter ? (
          getColorPalette(selectedFilter.colorHex)
        ) : (
          <Icon icon={props.filtersData[id].iconName} size={IconSize.SIZE_ICON_16} />
        )}
        <Text className={styles.text} val={isFilterSelected && selectedFilter ? selectedFilter.displayName : props.filtersData[id].displayName} size={TextSize.XSMALL} bold />
        {isFilterSelected ? (
          <Icon
            icon="icon-close"
            onClick={(e): void => {
              removeFilterForId(id);
              e.stopPropagation();
            }}
            size={IconSize.SIZE_ICON_16}
            className="content-body spacing-m-l-2"
          />
        ) : null}
      </div>
    );
  };

  const getSkeletonStates = (): ReactElement<any>[] => {
    const NUM_OF_SKELETONS = 4;
    const skeletons: ReactElement<any>[] = [];

    for (let i = 0; i < NUM_OF_SKELETONS; i++) {
      skeletons.push(<SkeletonBrick key={i} height={32} width={80} className={styles.skeletons} radius="1000px" />);
    }

    return skeletons;
  };

  const prepareFilters = (): ReactElement<any>[] | null => {
    if (!props.filtersData) {
      return getSkeletonStates();
    }

    const eachFilterId = Object.keys(props.filtersData);

    return eachFilterId.map((id) => {
      return (
        <Dropdown
          key={id}
          id={id}
          open={getOpenState(id)}
          onOpen={(): void => {
            onOpen(id);
          }}
          onClose={(): void => {
            onClose();
          }}
          doesCloseOnOuterClick={false}
          doCloseOnInnerClick={false}
          popupSpacingClass="spacing-p-t-2 spacing-p-l-2 spacing-p-r-2"
          alignment={ALIGNMENT_TYPE.BOTTOM_START}
          popUpHasCustomWidth={true}
          popupClassName={`${styles.popup} ${props.filtersData?.[id].isColorFilter ? styles.colorPopup : ''}`}
          className={styles.dropdownItems}
          position={DROPDOWN_POSITION.FIXED}
          selector={getSelector(id)}
          popup={getPopupForIdUpdated(id)}
          showModalForSmallScreen
          modalTitle={props.filtersData?.[id].displayName ?? ''}
          stopClickPropagation
        />
      );
    });
  };

  const onToggleSafeSearch = (): void => {
    props.onToggleSafeSearch(!props.safeSearch);
  };

  const getSafeSearchFilter = (): ReactElement<any> | null => {
    if (!props.filtersData || props.safeSearch === undefined) {
      return null;
    }
    return (
      <li
        onClick={(e): void => {
          setDropdownOpenState('');
          e.stopPropagation();
          e.preventDefault();
          onToggleSafeSearch();
        }}
        id="safe-search"
        key="safe-search"
        className={`${styles.items} ${props.safeSearch ? styles.appliedFilter : ''} hover-transition-all _cursor-pointer radius-round flex-center spacing-p-r-3 spacing-p-l-3 spacing-p-b-0 spacing-m-r-2`}
      >
        <Icon className={styles.icon} icon={props.safeSearch ? 'icon-eye-off' : 'icon-eye'} size={IconSize.SIZE_ICON_16} />
        <Text className={`${styles.text} spacing-m-r-2`} val={window.i18next.t('pmwjs_safe_search')} size={TextSize.XSMALL} bold />
        <ToggleSwitch isLoading={isSafeSearchBeingUpdated} isChecked={props.safeSearch} onChange={noop} size={ToggleSwitchV2Size.SMALL} />
      </li>
    );
  };

  const onToggleMultipage = (): void => {
    if (props.onToggleMultipage) {
      props.onToggleMultipage(!props.multipage);
    }
  };

  const getMultipageFilter = (): ReactElement<any> | null => {
    if (!props.filtersData || props.multipage === undefined || !props.onToggleMultipage || !isMultiPageEnabled) {
      return null;
    }
    return (
      <li
        onClick={(e): void => {
          setDropdownOpenState('');
          e.stopPropagation();
          e.preventDefault();
          onToggleMultipage();
        }}
        id="multipage"
        key="multipage"
        className={`${styles.items} ${props.multipage ? styles.appliedFilter : ''} hover-transition-all _cursor-pointer radius-round flex-center spacing-p-r-3 spacing-p-l-3 spacing-p-b-0 spacing-m-r-2`}
      >
        <Icon className={styles.icon} icon="icon-pages" size={IconSize.SIZE_ICON_16} />
        <Text className={`${styles.text} spacing-m-r-2`} val={window.i18next.t('pmwjs_multipage')} size={TextSize.XSMALL} bold />
        <ToggleSwitch isLoading={isMultipageBeingUpdated} isChecked={props.multipage} onChange={noop} size={ToggleSwitchV2Size.SMALL} />
      </li>
    );
  };

  return (
    <ul ref={containerRef} className={`flex-h-row spacing-m-l-4 spacing-m-r-4 spacing-m-b-0 ${styles.list}`}>
      {prepareFilters()}
      {getMultipageFilter()}
      {getSafeSearchFilter()}
      <TemplateFiltersScrollButton containerRef={containerRef} />
    </ul>
  );
}
